import axios from 'axios';
import { get, map } from 'lodash';

import { ENVIRONMENTS } from 'constants/environments';
import Customer from 'utils/Customer';
import Log from 'utils/Log';
import { CURRENCIES } from 'utils/Money';
import Settings from 'utils/Settings';
import Validator from 'utils/Validator';

import {
  ITERABLE_API_KEY_PROD,
  ITERABLE_API_KEY_STAGING,
  SIGNUP_CHANNEL,
  ENDPOINTS,
  BENELUX_CONSENT_LIST_IDS,
  ITERABLE_LISTS_API_KEY_PROD,
  CONSENT_LIST_STATUS,
} from './constants';

const API_KEY =
  Settings.environment === ENVIRONMENTS.STAGING || Settings.environment === ENVIRONMENTS.DEVELOPMENT
    ? ITERABLE_API_KEY_STAGING
    : ITERABLE_API_KEY_PROD;

const Iterable = class {
  static push = payload => {
    const { email, signupSource } = payload;

    if (!email) {
      throw new Error('Util:Iterable -- payload.email is required!');
    }

    if (!signupSource) {
      throw new Error('Util:Iterable -- payload.signupSource is required!');
    }

    window._iaq = window._iaq || [];
    window._iaq.push(['account', API_KEY]);
    window._iaq.push(['identify', email, payload]);
    window._iaq.push(['track', signupSource, { signupChannel: SIGNUP_CHANNEL }]);
  };

  static formatPriceToFloat(price) {
    return Number((price / 100).toFixed(2));
  }

  // we have to do this weird format bc the shopify provided line items are different
  // than what we use in state
  static formatShopifyProvidedCartItems = items => {
    return map(items, cartItem => {
      return {
        id: get(cartItem, 'variant_id'),
        sku: get(cartItem, 'sku'),
        name: `${get(cartItem, 'name')} - ${get(cartItem, 'color')}`,
        price: Iterable.formatPriceToFloat(get(cartItem, 'unformattedPrice')),
        quantity: get(cartItem, 'quantity'),
        productId: window.btoa(`gid://shopify/ProductVariant/${get(cartItem, 'product_id')}`),
        variantId: window.btoa(`gid://shopify/ProductVariant/${get(cartItem, 'variant_id')}`),
        productImage: get(cartItem, ['featuredImage', 'src']),
        handle: get(cartItem, 'handle'),
      };
    });
  };

  static updateCart = data => {
    const userEmail = Customer.email;

    const payload = {
      user: {
        email: userEmail,
      },
      ...data,
    };

    Iterable.callIterable({ email: userEmail, payload, endpoint: ENDPOINTS.UPDATE_CART });
  };

  static checkoutEvent = (email, lineItems, checkoutToken) => {
    const currencyCode = Settings.currency;

    const userEmail = email || Customer.email;

    const payload = {
      email: userEmail,
      eventName: 'createCheckout',
      dataFields: {
        currency: currencyCode,
        currencySymbol: CURRENCIES[currencyCode].units.symbol,
        source: 'web',
        shopifyStoreDomain: window.location.origin,
        checkoutType: 'Web Checkout',
        locale: Settings.currentLocale,
        checkoutId: checkoutToken,
        checkoutUrl: window.location.href,
        cartLineItems: Iterable.formatShopifyProvidedCartItems(lineItems),
      },
    };

    Iterable.callIterable({ email: userEmail, payload, endpoint: ENDPOINTS.TRACK });
  };

  static outOfStockEvent = checkoutToken => {
    const userEmail = Customer.email;

    const payload = {
      email: userEmail,
      eventName: 'checkoutFailure',
      dataFields: {
        source: 'web',
        shopifyStoreDomain: window.location.origin,
        checkoutId: checkoutToken,
        failureReason: 'stock_problems',
      },
    };

    Iterable.callIterable({ email: userEmail, payload, endpoint: ENDPOINTS.TRACK });
  };

  static handleBeneluxConsent = async (email, decision) => {
    const listId =
      decision === CONSENT_LIST_STATUS.ACCEPTED
        ? BENELUX_CONSENT_LIST_IDS.BENELUX_CONSENT
        : BENELUX_CONSENT_LIST_IDS.BENELUX_NO_CONSENT;
    const oppositeListId =
      decision === CONSENT_LIST_STATUS.ACCEPTED
        ? BENELUX_CONSENT_LIST_IDS.BENELUX_NO_CONSENT
        : BENELUX_CONSENT_LIST_IDS.BENELUX_CONSENT;

    try {
      // First, unsubscribe from the opposite list
      const unsubscribePayload = {
        listId: oppositeListId,
        subscribers: [{ email }],
      };

      const unsubscribeResult = await Iterable.callIterable({
        email,
        payload: unsubscribePayload,
        endpoint: ENDPOINTS.UNSUBSCRIBE,
        apiKey: ITERABLE_LISTS_API_KEY_PROD,
      });

      if (
        !unsubscribeResult ||
        !unsubscribeResult.successCount ||
        unsubscribeResult.failCount > 0
      ) {
        throw new Error(`Failed to unsubscribe from list: ${JSON.stringify(unsubscribeResult)}`);
      }

      // Then subscribe to the new list
      const subscribePayload = {
        listId,
        subscribers: [{ email }],
      };

      const subscribeResult = await Iterable.callIterable({
        email,
        payload: subscribePayload,
        endpoint: ENDPOINTS.SUBSCRIBE,
        apiKey: ITERABLE_LISTS_API_KEY_PROD,
      });

      if (!subscribeResult || !subscribeResult.successCount || subscribeResult.failCount > 0) {
        throw new Error(`Failed to subscribe to list: ${JSON.stringify(subscribeResult)}`);
      }

      // Verify both operations were successful
      if (unsubscribeResult.successCount >= 1 && subscribeResult.successCount >= 1) {
        return {
          success: true,
          unsubscribeResult,
          subscribeResult,
        };
      }

      throw new Error(
        `Unexpected response from Iterable API: ${JSON.stringify(subscribeResult)} ${JSON.stringify(unsubscribeResult)}`
      );
    } catch (error) {
      Log.error('Benelux subscription update failed:', error);
      throw error;
    }
  };

  static callIterable = ({ email, payload, endpoint, apiKey }) => {
    const appKey = apiKey || API_KEY;

    if (!email || !Validator.isEmail(email)) {
      return null;
    }

    if (!endpoint) {
      return null;
    }

    if (!appKey) {
      Log.error('Missing Iterable app key.');
    }

    return axios
      .post(
        endpoint,
        {
          ...payload,
        },
        {
          params: {
            api_key: appKey,
          },
        }
      )
      .then(({ data }) => data)
      .catch(err => {
        Log.error('Iterable Request Failed', err);
      });
  };
};

export default Iterable;
